import type { ReactNode } from "react";
import { Link } from "react-router";
import { CopyButton } from "./CopyButton";
import { Icon } from "./icons";
import { Tabs } from "./ui";

/**
 * The header both detail pages wear: apps and databases.
 *
 * Written once because the two were drifting — same job, two layouts, and a
 * customer moving between them had to re-learn where the name, the state and
 * the actions live. What it lays out, in the order a PaaS console is read:
 *
 *   1. one link back, since there is only one place to go
 *   2. the name and what the thing is doing right now
 *   3. the address, which on an app is the whole point of the page
 *   4. the actions
 *   5. the facts that do not change often, on one line rather than in a card
 *
 * The address row is the part the app detail page was missing. The live URL
 * sat in a card at the bottom of a sidebar, below the runtime panel — on every
 * other platform of this kind it is the first thing under the title, because
 * it is what someone came to click.
 */
export interface MetaItem {
  label: string;
  /** Rendered as-is, so a value can carry a link or a badge of its own. */
  value: ReactNode;
}

export function DetailHeader({
  backTo,
  backLabel,
  name,
  badge,
  address,
  actions,
  meta,
  tabs,
}: {
  backTo: string;
  backLabel: string;
  name: string;
  badge?: ReactNode;
  /** The thing to copy, and optionally to open. */
  address?: { value: string; href?: string; copyLabel: string; empty?: ReactNode };
  actions?: ReactNode;
  meta: MetaItem[];
  /**
   * Omitted by a detail page that is one page.
   *
   * A lone underlined tab is a control that cannot be used — it says there is
   * somewhere else to go and there is not. Tunnels have no second view, so
   * they wear the rest of this header and skip the row.
   */
  tabs?: { to: string; label: string; end?: boolean }[];
}) {
  return (
    <div className="space-y-5">
      <Link
        to={backTo}
        className="inline-flex items-center gap-1.5 text-sm text-fg-muted transition-colors duration-200 ease-smooth hover:text-fg"
      >
        <Icon name="chevron" className="size-3.5 rotate-180" />
        {backLabel}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="min-w-0 break-words text-[1.375rem] font-normal tracking-[-0.022em] text-fg sm:truncate">
              {name}
            </h1>
            {badge}
          </div>

          {address && (
            <div className="mt-2 flex min-w-0 items-center gap-3 text-sm">
              {address.href ? (
                <a
                  href={address.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex min-w-0 items-center gap-1.5 font-mono text-accent transition-colors duration-200 ease-smooth hover:text-accent-hover"
                >
                  <span className="break-all sm:truncate">{address.value}</span>
                  <Icon name="link" className="size-3.5 shrink-0" />
                </a>
              ) : address.value ? (
                <span className="break-all font-mono text-fg-muted sm:truncate">{address.value}</span>
              ) : (
                address.empty
              )}
              {address.value && (
                <CopyButton value={address.value} label={address.copyLabel} />
              )}
            </div>
          )}
        </div>

        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>

      {/*
        A row, not a card. These are the facts you glance at and stop looking
        at — putting them in a panel of their own gave them the same weight as
        the runtime state, which changes minute to minute and matters far more.
      */}
      <dl className="flex flex-wrap gap-x-10 gap-y-4 border-t border-line pt-5">
        {meta.map((item) => (
          <div key={item.label} className="min-w-0">
            <dt className="micro">{item.label}</dt>
            <dd className="mt-1.5 break-words text-sm text-fg sm:truncate">{item.value}</dd>
          </div>
        ))}
      </dl>

      {tabs && tabs.length > 0 && <Tabs items={tabs} />}
    </div>
  );
}
